import { Button, Form, Input, InputNumber, Space } from "antd";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog } from "zhihao-ui-react";

import type { ReceivePayment } from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE } from "@zhihaoscm/meta";

import "./editPaymentDialog.scss";

export interface ReceivePaymentUpdatePayload {
    amount: number;
    remark?: string;
    scheduleId?: string;
    paymentType?: number;
    ton?: number;
    tonProveFileId?: number;
}

export interface EditPaymentDialogProps {
    open: boolean;
    data?: ReceivePayment;
    onCancel: () => void;
    onSubmit: (data: ReceivePaymentUpdatePayload) => Promise<void> | void;
}

const PAYMENT_TYPE_LABELS: Record<number, string> = {
    1: RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.intro,
    2: RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.intro,
    3: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro,
    4: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro,
    5: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
};

interface EditPaymentValues {
    amount: number;
    remark?: string;
}

/** 对齐旧 Vue 收款编辑弹窗，允许修改金额和费用说明并补齐后端依赖的业务字段。 */
export default function EditPaymentDialog({ open, data, onCancel, onSubmit }: EditPaymentDialogProps) {
    const [form] = Form.useForm<EditPaymentValues>();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !data) return;
        form.setFieldsValue({ amount: data.amount, remark: data.remark });
    }, [data, form, open]);

    /** 表单验证通过后仅发送旧 Vue updatePayment 接口所需字段。 */
    const submit = async () => {
        if (!data) return;
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            await onSubmit({
                ...values,
                scheduleId: data.scheduleId || undefined,
                paymentType: data.paymentType || undefined,
                ton: data.ton || undefined,
                tonProveFileId: data.tonProveFileId || undefined
            });
        } finally {
            setSubmitting(false);
        }
    };

    const paymentTypeLabel = PAYMENT_TYPE_LABELS[Number(data?.paymentType)] || "收款";

    return (
        <ZhDialog
            alignCenter
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button aria-label="确认修改" loading={submitting} onClick={() => void submit()} type="primary">
                        确定
                    </Button>
                </Space>
            }
            onCancel={onCancel}
            open={open}
            rootClassName="trade-edit-payment-dialog-root"
            title={`修改${paymentTypeLabel}`}
            width={600}
        >
            <Form form={form} labelCol={{ flex: "79px" }}>
                <Form.Item label="费用类型">{paymentTypeLabel}</Form.Item>
                <Form.Item
                    label="支付金额"
                    name="amount"
                    rules={[
                        { required: true, message: "请输入支付金额" },
                        { type: "number", min: 0.01, max: 999999999.99, message: "请输入0~1000000000的数字" }
                    ]}
                >
                    <InputNumber
                        aria-label="支付金额"
                        controls={false}
                        max={999999999.99}
                        min={0.01}
                        placeholder="请输入支付金额"
                        style={{ width: 193 }}
                        suffix="元"
                    />
                </Form.Item>
                <Form.Item label="费用说明" name="remark">
                    <Input.TextArea
                        aria-label="费用说明"
                        maxLength={200}
                        placeholder="请输入费用说明"
                        rows={4}
                        showCount
                    />
                </Form.Item>
            </Form>
        </ZhDialog>
    );
}
